import React, { useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import Header from "../../Header";
import { User } from "../../../types/admin";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  IconLookup,
  IconDefinition,
  findIconDefinition,
} from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { AdminApi } from "../../../Api/AdminApi";
import ConfirmationModal from "../../../Components/Utils/ConfirmationModal";
import Pagination from "../../Utils/pagination";
import UserFilter, { UserFilters } from "../../../Components/Filters/UserFilter";
import SortableHeader from "../../../Components/Table/sortableHeader";

library.add(faTrashCan);
const trashLookup: IconLookup = { prefix: "fas", iconName: "trash-can" };
const trashcanDefinition: IconDefinition = findIconDefinition(trashLookup);

function ListOfUsers() {
  const { instance, accounts } = useMsal();
  const api = new AdminApi(accounts[0], instance);
  const navigate = useNavigate();
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);

  // État pour forcer le fetch manuel
  const [fetchCounter, setFetchCounter] = useState(0);

  // Pagination state - fixed page size of 20
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20; // Fixed page size
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasInitialResponse, setHasInitialResponse] = useState<boolean>(false);

  // Add state for user filters
  const [userFilters, setUserFilters] = useState<UserFilters>({
    names: [],
    projects: [],
    beneficiaries: [],
    azureIds: [],
    emails: [],
  });

  // Nouvel état pour le tri backend
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  // État pour stocker les utilisateurs
  const [users, setUsers] = useState<User[]>([]);

  // Fonction pour gérer les erreurs HTTP
  const handleHttpError = (error: any) => {
    // Vérifier si c'est une erreur 403
    if (error?.response?.status === 403 || error?.status === 403) {
      // Rediriger vers une page d'accès refusé ou la page d'accueil
      navigate('/access-denied'); // ou navigate('/') pour la page d'accueil
      return true; // Indique que l'erreur a été gérée
    }
    return false; // L'erreur n'a pas été gérée
  };

  // Handle API response structure
  const fetchUsers = async () => {
    if (!isMounted.current) return;
    
    setIsLoading(true);
    try {
      // Get data from API - using fixed page size of 20 and applying filters and sorting
      // Note: API update needed here (not implemented yet)
      const response = await api.getUsers(
        currentPage, 
        pageSize, 
        userFilters.names, 
        userFilters.projects, 
        userFilters.beneficiaries, 
        userFilters.azureIds,
        userFilters.emails,
        sortField || undefined,
        sortDirection || undefined
      );
      
      if (!isMounted.current) return;
      
      // Marquer qu'on a reçu une réponse du serveur
      setHasInitialResponse(true);
      
      // Check if response has expected structure (for paginated API)
      if (response && response.items && Array.isArray(response.items)) {
        // If we have a paginated response
        setUsers(response.items);
        setTotalItems(response.totalCount || 0);
        // Important: Use the totalPages value from API directly if available
        setTotalPages(response.totalPages || Math.ceil((response.totalCount || 0) / pageSize));
      } else if (Array.isArray(response)) {
        // If API returns an array directly (old format)
        setUsers(response);
        setTotalItems(response.length);
        setTotalPages(Math.ceil(response.length / pageSize));
      } else {
        // Fallback for unexpected formats
        console.error("Unexpected API response format:", response);
        setUsers([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      if (isMounted.current) {
        // Marquer qu'on a reçu une réponse du serveur (même si c'est une erreur)
        setHasInitialResponse(true);
        
        // Gérer l'erreur 403
        const handled = handleHttpError(error);
        if (!handled) {
          console.error("Error fetching users:", error);
          setUsers([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // Load users when fetchCounter changes - centralise control via this counter
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCounter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handler for page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Forcer le fetch avec un compteur
    setFetchCounter(prev => prev + 1);
  };

  // Handler for filter changes
  const handleFiltersChange = (filters: UserFilters) => {
    // Reset to first page when filters change
    setCurrentPage(0);
    // Update filters state
    setUserFilters(filters);
    // Forcer le fetch avec un compteur
    setFetchCounter(prev => prev + 1);
  };

  // Gestionnaire de tri
  const sortData = (field: keyof User) => {
    // Si on clique sur le même champ
    if (sortField === field) {
      // Cycle: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      // Nouveau champ de tri: commencer par asc
      setSortField(field);
      setSortDirection('asc');
    }
    
    // Reset to first page when sorting changes
    setCurrentPage(0);
    
    // Forcer le fetch avec un compteur - APRÈS avoir mis à jour les états
    setFetchCounter(prev => prev + 1);
  };

  // Fonction pour obtenir l'indicateur de tri
  const getSortIndicator = (field: keyof User) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const handleDeleteUser = async (id: string) => {
    setUserToDelete(id);
    setIsModalOpen(true);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        await api.deleteUser(userToDelete);
        setIsModalOpen(false);
        // Forcer le fetch avec un compteur
        setFetchCounter(prev => prev + 1);
      } catch (error) {
        // Gérer l'erreur 403 pour la suppression aussi
        const handled = handleHttpError(error);
        if (!handled) {
          console.error("Error deleting user:", error);
          // Vous pouvez ajouter ici une notification d'erreur pour l'utilisateur
        }
        setIsModalOpen(false);
      }
    }
  };

  const handleExportCsv = async () => {
    try {
      setIsLoading(true); 
  
      await api.exportUsersCsv(
        userFilters.names,
        userFilters.projects, 
        userFilters.beneficiaries, 
        userFilters.azureIds,
        userFilters.emails,
        sortField || undefined,
        sortDirection || undefined
      );
  
    } catch (error) {
      // Gérer l'erreur 403 pour l'export aussi
      const handled = handleHttpError(error);
      if (!handled) {
        console.error("Error exporting users to CSV:", error);
      }
    } finally {
      setIsLoading(false); 
    }
  };

  return (
    <>
      {!hasInitialResponse ? (
        // Afficher un écran de chargement complet tant qu'on n'a pas eu de réponse du serveur
        <div className="full-screen-loading">
          <div className="text-center">Loading...</div>
        </div>
      ) : (
        <>
          <Header projectName="" loaded={false} isAdmin={true} />
          <div className="project-container">
            <div className="button-holder">
              <input
                className="big-button mx-auto mt-3"
                type="submit"
                value="Export csv"
                onClick={handleExportCsv}
              />
            </div>
            <div className="feedstock-container">
              <h1 className="page-title">Users</h1>
              <UserFilter onFiltersChange={handleFiltersChange} />
              {isLoading ? (
                <div className="text-center my-4">Loading...</div>
              ) : (
                <>
                  <table className="table mt-5">
                    <thead>
                      <tr>
                        <SortableHeader<User>
                          label="Name"
                          sortKey="name"
                          sortData={sortData}
                          getSortIndicator={getSortIndicator}
                        />
                        <SortableHeader<User>
                          label="Email"
                          sortKey="email"
                          sortData={sortData}
                          getSortIndicator={getSortIndicator}
                        />
                        <SortableHeader<User>
                          label="Azure ID"
                          sortKey="id"
                          sortData={sortData}
                          getSortIndicator={getSortIndicator}
                        />
                        <SortableHeader<User>
                          label="Actions"
                          sortKey="id"
                          sortData={sortData}
                          getSortIndicator={getSortIndicator}
                          isAction={true}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center">No users found</td>
                        </tr>
                      ) : (
                        users.map((user, index) => (
                          <tr key={index}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.azureAdId}</td>
                            <td>
                              <a
                                className="action-button"
                                href={`/admin/User/${user.id}`}
                              >
                                <span>Edit user</span>
                              </a>
                              <a
                                className="action-button delete-button"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <FontAwesomeIcon icon={trashcanDefinition} />
                                <span>Delete user</span>
                              </a>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  
                  <div className="pagination-wrapper">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </div>
            
            
            <ConfirmationModal
              isOpen={isModalOpen}
              onRequestClose={() => setIsModalOpen(false)}
              onConfirm={confirmDelete}
              title="Confirm Deletion"
              message="Are you sure you want to delete this user? This action cannot be undone."
              confirmButtonText="Delete"
              cancelButtonText="Cancel"
              requireComment={false}
            />
          </div>
        </>
      )}
    </>
  );
}

export default ListOfUsers;